import { Grid, IconButton, Paper, Popper, Typography } from "@mui/material"
import React, { useCallback, useContext, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { customPalette } from "../../../../theme"
import { pagesUrl } from "../../../core/appConstants"
import { DepartmentContext } from "../../../core/context/DepartmentContext"
import { ProjectContext } from "../../../core/context/project/ProjectContext"
import { BSVariant } from "../../../core/dto/beem-shot/BSVariant/BSVariant"
import { stringToDate } from "../../../core/services/date-service"
import { resolveUrl } from "../../../core/services/http-service"
import { BSVariantStatusChip } from "../../badges/BSVariantStatusChip"
import { CreatedByDisplayer } from "../../CreatedByDisplayer"
import { IconNotes } from "../../custom-icons/icon-notes"
import { TabEnum } from "../../tabs/tabs"
import { DefaultTypography } from "../../typography/default-typography"
import { truncateTextMultiligneCss } from "../../../core/services/helper-service"

interface IProps {
  bsVariant: BSVariant
}

export function BSVariantCard({ bsVariant }: Readonly<IProps>): React.JSX.Element {
  const { project } = useContext(ProjectContext)
  const { findDepartmentById } = useContext(DepartmentContext)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [open, setOpen] = useState(false)

  const navigateUrl = useMemo(
    () => resolveUrl(pagesUrl.VARIANT_DETAILS, [project?.id, bsVariant.id], { tab: TabEnum.BS_VARIANT_INFO }),
    [bsVariant.id, project?.id]
  )

  const handleMouseEnter: (event: { currentTarget: React.SetStateAction<HTMLElement | null> }) => void = useCallback(
    (event) => {
      setAnchorEl(event.currentTarget)
      setOpen(true)
    },
    []
  )

  const handleMouseLeave: () => void = useCallback(() => {
    setOpen(false)
  }, [])

  const departmentName = useMemo(() => {
    if (project?.departmentId) {
      const department = findDepartmentById(project?.departmentId)
      return department ? department.departmentName : ""
    } else {
      return ""
    }
  }, [findDepartmentById, project?.departmentId])

  return (
    <Link to={navigateUrl} style={{ textDecoration: "none", color: "inherit", width: "100%" }}>
      <Grid
        container
        height="100%"
        gap={1}
        sx={{
          backgroundColor: customPalette.white,
          borderRadius: 3,
          p: 3,
          boxShadow: 5,
          transition: "all 0.2s ease-in-out",
          ":hover": {
            cursor: "pointer",
            boxShadow: 10,
            transform: "translateY(-5px)",
          },
        }}>
        <Grid item xs={12} display="flex" alignItems="center" justifyContent="flex-end">
          <BSVariantStatusChip status={bsVariant?.variantStatus} clickable />
        </Grid>
        <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
          <DefaultTypography label={bsVariant.name} fontSize="16px" lineHeight="24px" fontWeight={600} />

          {bsVariant.description && (
            <IconButton
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              sx={{ background: customPalette.lightGray5, borderRadius: 3 }}>
              <IconNotes sx={{ color: customPalette.subtitleGray }} />
            </IconButton>
          )}
        </Grid>

        <Grid item xs={12}>
          <CreatedByDisplayer
            createdByUser={bsVariant.createdByUser}
            department={departmentName}
            lastModifiedDate={stringToDate(bsVariant.lastModifiedDate)}
          />
        </Grid>
      </Grid>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ zIndex: 1300 }}>
        <Paper elevation={3} style={{ padding: 8 }}>
          <Typography
            variant="body2"
            sx={{
              ...truncateTextMultiligneCss,
              maxWidth: "400px",
            }}>
            {bsVariant.description}
          </Typography>
        </Paper>
      </Popper>
    </Link>
  )
}
